import React from 'react';
import { FaExternalLinkAlt, FaGithub } from 'react-icons/fa';

function ProjectActions({ code, demo }) {
  if (!code && !demo) return null;
  const hasLiveDemo = demo && demo !== code;

  return (
    <div className="portfolioProjectActions">
      {code && <a href={code} target="_blank" rel="noreferrer"><FaGithub /> GitHub</a>}
      {hasLiveDemo && <a href={demo} target="_blank" rel="noreferrer"><FaExternalLinkAlt /> Live Demo</a>}
    </div>
  );
}

function ProjectVisual({ project }) {
  if (project.image) {
    return (
      <div className="portfolioFeaturedProject__image">
        <div className="portfolioMockup portfolioMockup--browser">
          <div className="portfolioMockup__bar">
            <span />
            <span />
            <span />
            <strong>{project.name}</strong>
          </div>
          <div className="portfolioMockup__screen">
            <img src={project.image} alt={`${project.name} project screenshot`} loading="lazy" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolioFeaturedProject__image portfolioFeaturedProject__image--product" aria-label={`${project.name} product preview`}>
      <div className="portfolioProductMockup" aria-hidden="true">
        <div className="portfolioProductMockup__header">
          <span>Ride Tracking</span>
          <strong>ThrottleSync</strong>
        </div>
        <div className="portfolioProductMockup__map">
          <span />
          <span />
          <span />
        </div>
        <div className="portfolioProductMockup__stats">
          {project.features.slice(0, 4).map((feature) => (
            <div key={feature}>
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeaturedProjects({ projects }) {
  return (
    <div className="portfolioFeaturedProjects">
      {projects.map((project, index) => {
        const featurePreview = project.features?.slice(0, 5) || [];
        return (
          <article className="portfolioFeaturedProject" key={project.id}>
            <ProjectVisual project={project} />
            <div className="portfolioFeaturedProject__body">
              <div className="portfolioProjectHeader">
                <div>
                  <span className="portfolioProjectIndex">{String(index + 1).padStart(2, '0')}</span>
                  <h3>{project.name}</h3>
                </div>
                <span className="portfolioProjectCategory">{project.category}</span>
              </div>

              <p className="portfolioProjectPitch">{project.solution}</p>

              <div className="portfolioProjectSnapshot">
                <div>
                  <span>Built For</span>
                  <p>{project.problem}</p>
                </div>
                <div>
                  <span>System Shape</span>
                  <p>{project.architecture}</p>
                </div>
              </div>

              {featurePreview.length > 0 && (
                <ul className="portfolioProjectFeaturePills" aria-label={`${project.name} key features`}>
                  {featurePreview.map((feature) => <li key={feature}>{feature}</li>)}
                </ul>
              )}

              <div className="portfolioProjectFooter">
                <div className="portfolioTags portfolioTags--small">
                  {project.stack.slice(0, 5).map((tech) => <span key={tech}>{tech}</span>)}
                </div>
                {project.status && <p className="portfolioProjectStatus">{project.status}</p>}
                <ProjectActions code={project.code} demo={project.demo} />
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function ProjectGrid({ groups }) {
  return (
    <div className="portfolioProjectGroups">
      {groups.map((group) => (
        <article className="portfolioProjectGroup" key={group.title}>
          <div className="portfolioProjectGroup__heading">
            <h3>{group.title}</h3>
            <p>{group.description}</p>
          </div>
          <div className="portfolioCompactProjects">
            {group.projects.map((project) => (
              <div className="portfolioCompactProject" key={`${group.title}-${project.name}`}>
                <h4>{project.name}</h4>
                <p>{project.solution}</p>
                {project.features?.length > 0 && (
                  <ul className="portfolioCompactFeatureList">
                    {project.features.slice(0, 4).map((feature) => <li key={feature}>{feature}</li>)}
                  </ul>
                )}
                <p className="portfolioCompactContribution">{project.contribution}</p>
                <div className="portfolioTags portfolioTags--small">
                  {project.stack.map((tech) => <span key={tech}>{tech}</span>)}
                </div>
                <ProjectActions code={project.code} demo={project.demo} />
              </div>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}

function Projects({ featuredProjects, projectGroups }) {
  return (
    <section className="portfolioSection sectionReveal" id="projects">
      <div className="portfolioSection__heading">
        <span className="portfolioEyebrow">Projects</span>
        <h2>Systems, products, and backend builds.</h2>
        <p>Selected work across product apps, desktop workflows, cloud integrations, APIs, and data-backed interfaces.</p>
      </div>
      <FeaturedProjects projects={featuredProjects} />
      <ProjectGrid groups={projectGroups} />
    </section>
  );
}

export default Projects;
